import React, { useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import CommissionPayPeriodSummaryItem from 'src/models/personnel/CommissionPayPeriodSummaryItem';
import CommissionPayPeriodTotalItem from 'src/models/personnel/CommissionPayPeriodTotalItem';
import CommissionPayStatusDetailItem from 'src/models/personnel/CommissionPayStatusDetailItem';

interface IProductionBonusHistoryDialogProps {
    dialogName: string;
    mgrNo?: number;
    summaryItems: Array<CommissionPayPeriodSummaryItem>;
    totalItems: Array<CommissionPayPeriodTotalItem>;
    detailItems: Array<CommissionPayStatusDetailItem>;
    onSelectPayrollPeriod: (payrollPeriod: number) => void;
    onSelectPeriodNo: (payrollPeriod: number, periodNo: number) => void;
    onClose: () => void;
}

const formatCurrency = (value: number) =>
    (value ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// Mirrors the real view's own "999999 payroll_period" catch-all bucket for not-yet-finalized
// (still-open) commission periods.
const formatPeriod = (value: number) => (value === 999999 ? 'PROJECTED' : String(value));

const formatDate = (value: Date | string) => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US');
};

const sumBy = <T, >(items: Array<T>, selector: (item: T) => number) =>
    items.reduce((total, item) => total + (selector(item) ?? 0), 0);

const rowStyle = (selected: boolean): React.CSSProperties => ({
    cursor: 'pointer',
    backgroundColor: selected ? '#d6e6f5' : undefined,
});

const totalsRowStyle: React.CSSProperties = { backgroundColor: '#8ee88e', fontWeight: 'bold' };

// Mirrors manager_commission.fmt's "Production Bonus History" window (canvas HIST_CONTROL, caption
// "Bonus History", opened via button B_PB_HISTORY) - three cascading grids, each a real
// master-detail of the one before it: Commission_Pay_Periods_Summary (by Payroll/Bonus Period) ->
// Commission_Pay_Periods_Total (by that period's own bi-weekly Period Nos) ->
// Commission_Pay_Status (flat detail rows for the selected Payroll Period + Period No). Backed by
// the same-named SQL Server views, ported 2026-09-15 from the real Oracle view DDLs (none of the
// three existed in SQL Server before). Excel Report (XL_PM_Commission_Pay) is not built yet.
const ProductionBonusHistoryDialog = (props: IProductionBonusHistoryDialogProps) => {
    const [selectedPayrollPeriod, setSelectedPayrollPeriod] = useState<number | undefined>(undefined);
    const [selectedPeriodNo, setSelectedPeriodNo] = useState<number | undefined>(undefined);

    const selectPayrollPeriod = (payrollPeriod: number) => {
        setSelectedPayrollPeriod(payrollPeriod);
        setSelectedPeriodNo(undefined);
        props.onSelectPayrollPeriod(payrollPeriod);
    };

    const selectPeriodNo = (periodNo: number) => {
        setSelectedPeriodNo(periodNo);
        if (selectedPayrollPeriod !== undefined) {
            props.onSelectPeriodNo(selectedPayrollPeriod, periodNo);
        }
    };

    const close = () => {
        setSelectedPayrollPeriod(undefined);
        setSelectedPeriodNo(undefined);
        props.onClose();
    };

    return (
        <Dialog title="Bonus History" dialogName={props.dialogName} bodyStyle={{ overflowY: 'hidden', width: '60%', height: '85vh'}} isExitButtonInvisible={true}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '.75rem' }}>
                <div>
                    <label style={{ fontWeight: 'bold', marginRight: '.5rem' }}>DLC Manager No.</label>
                    <input readOnly={true} value={props.mgrNo ?? ''} style={{ width: '8rem', textAlign: 'center' }} />
                </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 'bold', textAlign: 'center', marginBottom: '.25rem' }}>Production Bonus Pay Periods</div>
                    <div style={{ height: '33vh', overflowY: 'auto', border: '1px solid #ccc' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
                            <thead>
                                <tr>
                                    <th className="table-header">Bonus Period</th>
                                    <th className="table-header">Not Invoiced</th>
                                    <th className="table-header">Invoiced Amt</th>
                                    <th className="table-header">Invoice Paid</th>
                                    <th className="table-header">Bonus Paid</th>
                                    <th className="table-header">To Be Paid</th>
                                </tr>
                            </thead>
                            <tbody>
                                {props.summaryItems.map((item) => (
                                    <tr
                                        key={item.payrollPeriod}
                                        style={rowStyle(item.payrollPeriod === selectedPayrollPeriod)}
                                        onClick={() => selectPayrollPeriod(item.payrollPeriod)}
                                    >
                                        <td>{formatPeriod(item.payrollPeriod)}</td>
                                        <td>{formatCurrency(item.notInvoiced)}</td>
                                        <td>{formatCurrency(item.invoiced)}</td>
                                        <td>{formatCurrency(item.invoicePaid)}</td>
                                        <td>{formatCurrency(item.pbPaid)}</td>
                                        <td>{formatCurrency(item.toBePaid)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={totalsRowStyle}>
                                    <td>Total</td>
                                    <td>{formatCurrency(sumBy(props.summaryItems, (i) => i.notInvoiced))}</td>
                                    <td>{formatCurrency(sumBy(props.summaryItems, (i) => i.invoiced))}</td>
                                    <td>{formatCurrency(sumBy(props.summaryItems, (i) => i.invoicePaid))}</td>
                                    <td>{formatCurrency(sumBy(props.summaryItems, (i) => i.pbPaid))}</td>
                                    <td>{formatCurrency(sumBy(props.summaryItems, (i) => i.toBePaid))}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 'bold', textAlign: 'center', marginBottom: '.25rem' }}>Production Bonus Pay Period Summary</div>
                    <div style={{ height: '33vh', overflowY: 'auto', border: '1px solid #ccc' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
                            <thead>
                                <tr>
                                    <th className="table-header">Payroll Period</th>
                                    <th className="table-header">Not Invoiced</th>
                                    <th className="table-header">Invoiced Amt</th>
                                    <th className="table-header">Invoice Paid</th>
                                    <th className="table-header">Bonus Paid</th>
                                    <th className="table-header">To Be Paid</th>
                                </tr>
                            </thead>
                            <tbody>
                                {props.totalItems.length === 0 && (
                                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '.5rem' }}>Select a bonus period.</td></tr>
                                )}
                                {props.totalItems.map((item) => (
                                    <tr
                                        key={item.periodNo}
                                        style={rowStyle(item.periodNo === selectedPeriodNo)}
                                        onClick={() => selectPeriodNo(item.periodNo)}
                                    >
                                        <td>{item.periodNo}</td>
                                        <td>{formatCurrency(item.notInvoiced)}</td>
                                        <td>{formatCurrency(item.invoiced)}</td>
                                        <td>{formatCurrency(item.invoicePaid)}</td>
                                        <td>{formatCurrency(item.pbPaid)}</td>
                                        <td>{formatCurrency(item.toBePaid)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={totalsRowStyle}>
                                    <td>Total</td>
                                    <td>{formatCurrency(sumBy(props.totalItems, (i) => i.notInvoiced))}</td>
                                    <td>{formatCurrency(sumBy(props.totalItems, (i) => i.invoiced))}</td>
                                    <td>{formatCurrency(sumBy(props.totalItems, (i) => i.invoicePaid))}</td>
                                    <td>{formatCurrency(sumBy(props.totalItems, (i) => i.pbPaid))}</td>
                                    <td>{formatCurrency(sumBy(props.totalItems, (i) => i.toBePaid))}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: '1rem' }}>
                <div style={{ fontWeight: 'bold', textAlign: 'center', marginBottom: '.25rem' }}>Production Bonus Pay Period Detail</div>
                <div style={{ height: '33vh', overflowY: 'auto', border: '1px solid #ccc' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
                        <thead>
                            <tr>
                                <th className="table-header">Payroll Period</th>
                                <th className="table-header">Client</th>
                                <th className="table-header">Project</th>
                                <th className="table-header">LM No.</th>
                                <th className="table-header">Landman Name</th>
                                <th className="table-header">Invoice No.</th>
                                <th className="table-header">Inv. Paid Date</th>
                                <th className="table-header">Bonus Amount</th>
                                <th className="table-header">Not Invoiced</th>
                                <th className="table-header">Invoiced Amt</th>
                                <th className="table-header">Invoice Paid</th>
                                <th className="table-header">Bonus Paid</th>
                                <th className="table-header">To Be Paid</th>
                            </tr>
                        </thead>
                        <tbody>
                            {props.detailItems.length === 0 && (
                                <tr><td colSpan={13} style={{ textAlign: 'center', padding: '.5rem' }}>Select a payroll period above.</td></tr>
                            )}
                            {props.detailItems.map((item, index) => (
                                <tr key={`${item.clNo}-${item.prNo}-${item.tsdNo}-${index}`}>
                                    <td>{formatPeriod(item.payrollPeriod)}</td>
                                    <td>{item.clNo}</td>
                                    <td>{item.prNo}</td>
                                    <td>{item.tsdNo}</td>
                                    <td>{item.akaName}</td>
                                    <td>{item.invoiceNo}</td>
                                    <td>{formatDate(item.invoicePaid)}</td>
                                    <td>{formatCurrency(item.tbAmt)}</td>
                                    <td>{formatCurrency(item.notInvoiced)}</td>
                                    <td>{formatCurrency(item.invoiced)}</td>
                                    <td>{formatCurrency(item.invoicePaidAmt)}</td>
                                    <td>{formatCurrency(item.pbPaid)}</td>
                                    <td>{formatCurrency(item.toBePaid)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr style={totalsRowStyle}>
                                <td colSpan={7}>Total</td>
                                <td>{formatCurrency(sumBy(props.detailItems, (i) => i.tbAmt))}</td>
                                <td>{formatCurrency(sumBy(props.detailItems, (i) => i.notInvoiced))}</td>
                                <td>{formatCurrency(sumBy(props.detailItems, (i) => i.invoiced))}</td>
                                <td>{formatCurrency(sumBy(props.detailItems, (i) => i.invoicePaidAmt))}</td>
                                <td>{formatCurrency(sumBy(props.detailItems, (i) => i.pbPaid))}</td>
                                <td>{formatCurrency(sumBy(props.detailItems, (i) => i.toBePaid))}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div> 
                <div style={{marginTop: '.5rem', width: '100%', display: 'flex', justifyContent: 'center'}}>                    
                    <Button className="blue-hover-button" text="Close" onClick={() => close()} />
                </div>
            </div>
        </Dialog>
    );
};

export default ProductionBonusHistoryDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/ProductionBonusHistoryDialog.tsx